import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LayoutDashboard, LogOut, FileText, Target, Mic, UserCircle, ChevronDown, Send, Video, CreditCard } from 'lucide-react';
import Logo from './Logo';
import ContactModal from './ContactModal';
import LoginModal from './LoginModal';
import { useAuth } from '../context/AuthContext';
import { saveReturnUrlFromSearch } from '../utils/returnUrl';
import './Navbar.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  // ?login=1 parametresi varsa modal aç
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('login') === '1' && !isAuthenticated) {
      // Korumalı sayfadan geldiyse dönülecek adresi sakla
      saveReturnUrlFromSearch(location.search);
      setIsLoginOpen(true);
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location, isAuthenticated]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProfileOpen(false);
  }, [location]);

  // Dışarı tıklayınca dropdown kapat
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsProfileOpen(false);
    logout();
    navigate('/');
  };

  return (
    <nav className={`navbar ${isScrolled ? 'navbar--scrolled' : ''}`}>
      <div className="navbar__container">
        <Link to="/" className="navbar__logo">
          <Logo size="default" showDomain />
        </Link>

        <div className="navbar__links">
          <Link to="/" className={`navbar__link ${location.pathname === '/' ? 'navbar__link--active' : ''}`}>
            Ana Sayfa
          </Link>
          <Link to="/nasil-calisir" className={`navbar__link ${location.pathname === '/nasil-calisir' ? 'navbar__link--active' : ''}`}>
            Nasıl Çalışır?
          </Link>
          <button className="navbar__link navbar__link--btn" onClick={() => setIsContactOpen(true)}>İletişim</button>
        </div>

        <div className="navbar__actions">
          {isAuthenticated ? (
            <div className="navbar__profile" ref={profileRef}>
              <button className="navbar__profile-btn" onClick={() => setIsProfileOpen(!isProfileOpen)}>
                <div className="navbar__profile-avatar">
                  {user?.imageUrl ? (
                    <img src={user.imageUrl} alt="" />
                  ) : (
                    <User size={16} />
                  )}
                </div>
                <span className="navbar__profile-name">{user?.name || 'Profilim'}</span>
                <ChevronDown size={14} className={`navbar__profile-chevron ${isProfileOpen ? 'navbar__profile-chevron--open' : ''}`} />
              </button>

              {isProfileOpen && (
                <div className="navbar__dropdown">
                  <div className="navbar__dropdown-header">
                    <p className="navbar__dropdown-name">{user?.name} {user?.surname}</p>
                    <p className="navbar__dropdown-email">{user?.email}</p>
                  </div>
                  <div className="navbar__dropdown-divider" />
                  <Link to="/panel" className="navbar__dropdown-item" onClick={() => setIsProfileOpen(false)}>
                    <LayoutDashboard size={15} /> Kariyerim
                  </Link>
                  <Link to="/panel/profil" className="navbar__dropdown-item" onClick={() => setIsProfileOpen(false)}>
                    <UserCircle size={15} /> Profilim
                  </Link>
                  <Link to="/panel/basvurularim" className="navbar__dropdown-item" onClick={() => setIsProfileOpen(false)}>
                    <Send size={15} /> Başvurularım
                  </Link>
                  <Link to="/panel/cv-olustur" className="navbar__dropdown-item" onClick={() => setIsProfileOpen(false)}>
                    <FileText size={15} /> CV'lerim
                  </Link>
                  <Link to="/panel/uyum-analizi" className="navbar__dropdown-item" onClick={() => setIsProfileOpen(false)}>
                    <Target size={15} /> İlan Uyum Analizi
                  </Link>
                  <Link to="/panel/abonelik" className="navbar__dropdown-item" onClick={() => setIsProfileOpen(false)}>
                    <CreditCard size={15} /> Aboneliğim
                  </Link>
                  <Link to="/panel/toplantilarim" className="navbar__dropdown-item" onClick={() => setIsProfileOpen(false)}>
                    <Video size={15} /> Toplantı Notlarım
                  </Link>
                  <Link to="/panel/mulakat" className="navbar__dropdown-item" onClick={() => setIsProfileOpen(false)}>
                    <Mic size={15} /> Mülakat Pratiği
                  </Link>
                  <div className="navbar__dropdown-divider" />
                  <button className="navbar__dropdown-item navbar__dropdown-item--danger" onClick={handleLogout}>
                    <LogOut size={15} /> Çıkış Yap
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="navbar__btn navbar__btn--primary" onClick={() => setIsLoginOpen(true)}>
              <User size={16} />
              Üye Girişi
            </button>
          )}
        </div>

        <button
          className="navbar__mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="navbar__mobile-menu">
          <Link to="/" className="navbar__mobile-link">Ana Sayfa</Link>
          <Link to="/nasil-calisir" className="navbar__mobile-link">Nasıl Çalışır?</Link>
          <button className="navbar__mobile-link navbar__link--btn" onClick={() => setIsContactOpen(true)}>İletişim</button>
          {isAuthenticated ? (
            <>
              <Link to="/panel" className="navbar__mobile-link">Kariyerim</Link>
              <Link to="/panel/profil" className="navbar__mobile-link">Profilim</Link>
              <Link to="/panel/basvurularim" className="navbar__mobile-link">Başvurularım</Link>
              <Link to="/panel/cv-olustur" className="navbar__mobile-link">CV'lerim</Link>
              <Link to="/panel/uyum-analizi" className="navbar__mobile-link">İlan Uyum Analizi</Link>
              <Link to="/panel/abonelik" className="navbar__mobile-link">Aboneliğim</Link>
              <Link to="/panel/toplantilarim" className="navbar__mobile-link">Toplantı Notlarım</Link>
              <Link to="/panel/mulakat" className="navbar__mobile-link">Mülakat Pratiği</Link>
              <button className="navbar__mobile-link navbar__mobile-link--danger" onClick={handleLogout}>
                <LogOut size={15} /> Çıkış Yap
              </button>
            </>
          ) : (
            <button className="navbar__btn navbar__btn--primary navbar__btn--full" onClick={() => setIsLoginOpen(true)}>
              <User size={16} />
              Üye Girişi
            </button>
          )}
        </div>
      )}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
    </nav>
  );
};

export default Navbar;
